import { NodeSelection, Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'

import { InlineGenerationPrompt } from '../../components/InlineGenerationPrompt/InlineGenerationPrompt'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ArticleFlat, Layer } from 'types'
import { Provider } from 'react-redux'
import store from 'redux/store'
import { emptyPromptKey } from 'proseShared/plugins/EmptyPrompt/EmptyPrompt'
import { ModalStack } from 'almanac-ui/Modal'
import { Router } from 'react-router-dom'
import { useCustomHistory } from 'hooks/useHistory'
import { createRoot, Root } from 'react-dom/client'

export const AIInlinePromptPluginKey = new PluginKey('AIInlinePrompt')

export type AIInlinePromptPluginState = {
  article: ArticleFlat
  layer: Layer
  queryClient: QueryClient
}

const bootstrapAIPromptWidget = () => {
  const containerDom = document.createElement('div')
  return containerDom
}

export function AIInlinePromptPlugin({ article, layer, queryClient }: AIInlinePromptPluginState) {
  return new Plugin({
    key: AIInlinePromptPluginKey,

    props: {
      decorations(editorState) {
        return AIInlinePromptPluginKey.getState(editorState).decorations
      },
      handleKeyDown(view, event) {
        const isSpaceKeyPressed = event.key === ' '
        const emptyPromptState = emptyPromptKey.getState(view.state)
        const aiPromptState = AIInlinePromptPluginKey.getState(view.state)

        if (aiPromptState && aiPromptState.active) return false

        if (emptyPromptState && emptyPromptState.visible && isSpaceKeyPressed) {
          let position = view.state.selection.from

          // Get the resolved position and check its parent
          const resolvedPos = view.state.doc.resolve(position)
          const parent = resolvedPos.parent

          // If the parent node is an empty paragraph, set position to the start of the parent
          if (parent && parent.type.name === 'paragraph' && parent.content.size === 0) {
            position = resolvedPos.before() // Set to the start of the parent node
          }

          const transaction = view.state.tr.setMeta(AIInlinePromptPluginKey, {
            active: true,
            position: position,
          })
          view.dispatch(transaction)
          return true
        }
        return false
      },
    },
    state: {
      init(config) {
        const widgetContainer = bootstrapAIPromptWidget()
        const root = createRoot(widgetContainer)
        return {
          active: false,
          position: null,
          decorations: DecorationSet.create(config.doc, []),
          widgetContainer,
          article: article,
          layer: layer,
          queryClient,
          root,
        }
      },
      apply(tr, state) {
        const meta = tr.getMeta(AIInlinePromptPluginKey)
        if (!meta && !state.active) return state
        if (!meta && state.active && !tr.docChanged) return state

        let newPosition = state.position

        // If the plugin is active and the document has changed, update the position
        if (state.active && tr.docChanged && state.position !== null) {
          newPosition = tr.mapping.map(state.position)
        }

        // If the plugin's state is set to inactive, unmount the widget and remove its decorations
        if (meta && meta.active === false) {
          state.root.render(<></>)
          return {
            ...state,
            active: false,
            position: null,
            decorations: DecorationSet.empty,
          }
        }

        // If the plugin is being activated, use the position from the meta data
        if (meta && meta.active === true) {
          newPosition = meta.position // Use the new position from the meta
        }

        // Return the updated state with the new position and decorations
        return {
          ...state,
          active: meta ? meta.active : state.active,
          position: newPosition,
          decorations: DecorationSet.create(tr.doc, [
            ...createDecorations(state.widgetContainer, state.root, newPosition, state.article, state.queryClient),
          ]),
        }
      },
    },
  })
}

const createDecorations = (
  container: HTMLElement,
  root: Root,
  pos: number,
  article: ArticleFlat,
  queryClient: QueryClient,
) => {
  return [
    Decoration.widget(pos, renderAIPrompt(container, root, article, queryClient, pos), {
      key: 'AI_PROMPT_DECORATION_KEY',
    }),
  ]
}

const renderAIPrompt =
  (container: HTMLElement, root: Root, article: ArticleFlat, queryClient: QueryClient, pos: number) =>
  (view: EditorView) => {
    const history = useCustomHistory()
    const resolvedPos = view.state.doc.resolve(pos)
    const selection = new NodeSelection(resolvedPos)
    root.render(
      <Provider store={store}>
        <Router history={history}>
          <QueryClientProvider client={queryClient}>
            <ModalStack>
              <InlineGenerationPrompt
                article={article}
                editorView={view}
                hideAIDocBar={() => {
                  view.dispatch(view.state.tr.setMeta(AIInlinePromptPluginKey, { position: null, active: false }))
                }}
                selection={selection}
              />
            </ModalStack>
          </QueryClientProvider>
        </Router>
      </Provider>,
    )

    return container
  }
